import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  GuitarGameSession,
  type GuitarSessionStatus,
} from '../../game/guitar/guitarSession';
import {
  buildTutorialGuitarChart,
  getGuitarTutorialOverlays,
  type GuitarTutorialOverlay,
} from '../../game/guitar/tutorialGuitarChart';
import { markTutorialSeen } from '../../game/tutorialFlag';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { loadKeybindPrefs, maniaLaneCodes } from '../../game/keybindPrefs';
import '../play.css';
import './guitarTutorial.css';

const TUTORIAL_CHART = buildTutorialGuitarChart();

function pickOverlay(
  overlays: GuitarTutorialOverlay[],
  beat: number,
): GuitarTutorialOverlay {
  for (let i = overlays.length - 1; i >= 0; i -= 1) {
    const o = overlays[i];
    if (beat >= o.fromBeat && beat < o.untilBeat) {
      return o;
    }
  }
  return overlays[0];
}

/**
 * Quick onboarding for Guitar mode. Runs a hand-crafted teaching
 * chart through {@link GuitarGameSession} so a brand-new player
 * learns the "strum-when-the-gem-hits-the-bar" rhythm in a controlled
 * environment before being dropped into a real osu!mania chart.
 *
 * Mirrors the Piano tutorial's beat-synced overlay pattern (each
 * lesson chunk advances when the player's beat crosses a threshold).
 */
export function GuitarTutorialPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<GuitarGameSession | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [status, setStatus] = useState<GuitarSessionStatus>('idle');
  const isTouch = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: coarse)').matches,
    [],
  );
  const overlays = useMemo(
    () => getGuitarTutorialOverlays(isTouch),
    [isTouch],
  );
  const [overlay, setOverlay] = useState<GuitarTutorialOverlay>(overlays[0]);

  useEffect(() => {
    saveModePref('guitar');
  }, []);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (status === 'idle') {
      // No session running yet — skip the RAF loop entirely. The
      // overlay is stable until the player taps Start, and pumping
      // pickOverlay every frame against a null session is just
      // wasted wakeups for the tab.
      return undefined;
    }
    let raf = 0;
    const loop = () => {
      const session = sessionRef.current;
      if (session) {
        const beat = session.getCurrentBeat();
        if (Number.isFinite(beat)) {
          const next = pickOverlay(overlays, beat);
          setOverlay((cur) => (cur.fromBeat === next.fromBeat ? cur : next));
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [overlays, status]);

  const startTutorial = useCallback(async () => {
    if (!canvasRef.current) {
      return;
    }
    setNeedsTap(false);
    if (sessionRef.current) {
      await sessionRef.current.destroy();
      sessionRef.current = null;
    }
    const session = new GuitarGameSession({
      chart: TUTORIAL_CHART,
      canvas: canvasRef.current,
      audioOffsetMs: 0,
      onStatusChange: (s) => setStatus(s),
      onComplete: () => {
        // Any tutorial play-through — pass or fail — counts as
        // "seen" so the home-page gate doesn't trap the player in a
        // loop. Mirrors the Piano tutorial's behaviour.
        markTutorialSeen('guitar');
      },
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch (err) {
      console.error('Failed to start Guitar tutorial:', err);
      setNeedsTap(true);
    }
  }, []);

  useEffect(() => {
    if (!needsTap) {
      return;
    }
    const laneCodes = maniaLaneCodes(loadKeybindPrefs(), 'guitar');
    const onKey = (e: KeyboardEvent) => {
      // Any of the player's currently-bound lane keys (or
      // Space / Enter) starts the run — pre-mount lane-key presses
      // feel rewarding rather than ignored. Reading from prefs
      // here keeps the tutorial honest after a rebind.
      if (
        e.key === ' ' ||
        e.code === 'Space' ||
        e.key === 'Enter' ||
        laneCodes.has(e.code)
      ) {
        e.preventDefault();
        void startTutorial();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, startTutorial]);

  const skipTutorial = () => {
    markTutorialSeen('guitar');
    navigate(modePath('guitar'));
  };

  return (
    <div className="play-page tutorial-page" data-testid="guitar-tutorial-page">
      <div className="play-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="play-canvas"
          data-testid="guitar-tutorial-canvas"
          onClick={() => {
            if (needsTap) {
              void startTutorial();
            }
          }}
        />

        {needsTap && (
          <div className="play-overlay" data-testid="guitar-tutorial-start-overlay">
            <div className="play-overlay-card">
              <h2>Welcome to Guitar</h2>
              <GuitarTutorialDemo />
              <p>
                {isTouch ? 'Tap each lane' : (
                  <>
                    Press <kbd>D</kbd> <kbd>F</kbd> <kbd>J</kbd> <kbd>K</kbd>
                    {' '}(or <kbd>Q</kbd> <kbd>W</kbd> <kbd>E</kbd> <kbd>R</kbd>)
                  </>
                )}{' '}
                as the gems strike the strum bar. Hold gems trail a ribbon —
                keep your finger down until the top of the ribbon meets the
                bar.
              </p>
              <p className="muted faint">
                Short tutorial &middot; headphones recommended.
              </p>
              <div className="play-overlay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={() => void startTutorial()}
                  data-testid="guitar-tutorial-start"
                >
                  Start tutorial
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={skipTutorial}
                  data-testid="guitar-tutorial-skip"
                >
                  Skip &mdash; take me to the songs
                </button>
              </div>
            </div>
          </div>
        )}

        {(status === 'completed' || status === 'failed') && (
          <div
            className="play-overlay"
            data-testid="guitar-tutorial-complete-overlay"
          >
            <div className="play-overlay-card">
              <h2>{status === 'completed' ? "You're ready" : 'Try again?'}</h2>
              <p>
                {status === 'completed'
                  ? 'Pick a Normal-tier track to start collecting stars — every chart has a 1-dot Normal you can warm up on.'
                  : 'Take another shot — Guitar normal-tier still gives you plenty of room.'}
              </p>
              <div className="play-overlay-actions">
                <Link
                  to={modePath('guitar')}
                  className="btn btn-primary btn-large"
                >
                  Browse tracks
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setNeedsTap(true);
                    setStatus('idle');
                  }}
                >
                  Replay tutorial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/*
       * Sibling of the canvas wrap (not a child) so on narrow viewports the
       * overlay flows below the playfield instead of covering it. On wider
       * viewports CSS lifts it back into a floating overlay anchored to the
       * canvas — see .tutorial-overlay in play.css.
       */}
      {!needsTap && status !== 'completed' && status !== 'failed' && (
        <div
          className="tutorial-overlay"
          data-testid="guitar-tutorial-overlay-text"
        >
          <div className="tutorial-overlay-card">
            <h3>{overlay.title}</h3>
            <p>{overlay.body}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Looped 3-second SVG demo shown above the "Start tutorial" button —
 * a 4-lane perspective fretboard with a gem cruising down lane 2,
 * a pulsing strum bar at the bottom, and "STRUM!" callout on
 * arrival. Mirrors the live playfield (perspective trapezoid, gems
 * descending toward a horizontal bar) so the demo sets the same
 * mental model the player will see when the tutorial starts.
 *
 * Animation lives in `guitarTutorial.css` keyframes; the SVG geometry
 * here is intentionally simple so the timing is easy to read and
 * tune.
 */
function GuitarTutorialDemo() {
  return (
    <svg
      viewBox="0 0 320 130"
      className="guitar-tutorial-demo"
      role="img"
      aria-label="Demo: strum each falling gem when it reaches the strum bar"
      data-testid="guitar-tutorial-demo"
    >
      <defs>
        <linearGradient id="gt-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1a0410" />
          <stop offset="1" stopColor="#3a0a25" />
        </linearGradient>
        <linearGradient id="gt-strum" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(255, 184, 59, 0.85)" />
          <stop offset="1" stopColor="#ffae3b" />
        </linearGradient>
        <linearGradient id="gt-gem" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffb6c1" />
          <stop offset="1" stopColor="#ff3b5c" />
        </linearGradient>
      </defs>
      <rect width="320" height="130" fill="url(#gt-bg)" />
      {/* Trapezoid highway. */}
      <polygon
        points="100,4 220,4 280,118 40,118"
        className="guitar-tutorial-demo-highway"
      />
      {/* Lane stripes (4) — drawn as four narrow trapezoid slices. */}
      {[0, 1, 2, 3].map((laneIdx) => {
        const tl = 100 + laneIdx * 30;
        const bl = 40 + laneIdx * 60;
        return (
          <polygon
            key={laneIdx}
            points={`${tl},4 ${tl + 30},4 ${bl + 60},118 ${bl},118`}
            className={`guitar-tutorial-demo-lane guitar-tutorial-demo-lane-${laneIdx}`}
          />
        );
      })}
      <line
        className="guitar-tutorial-demo-strumbar"
        x1="40"
        y1="112"
        x2="280"
        y2="112"
      />
      {/* Falling gem in lane 2 (J — third from left). */}
      <g className="guitar-tutorial-demo-gem">
        <rect
          x="160"
          y="0"
          width="34"
          height="20"
          rx="6"
          fill="url(#gt-gem)"
          stroke="#ffffff"
          strokeWidth="1.4"
          opacity="0.95"
        />
      </g>
      {/* Fret caps along the strum bar. */}
      {[
        { x: 70, color: '#3bff7a' },
        { x: 130, color: '#ff3b5c' },
        { x: 190, color: '#ffd84d' },
        { x: 250, color: '#3b9dff' },
      ].map((c, i) => (
        <circle
          key={i}
          cx={c.x}
          cy="118"
          r="9"
          fill={c.color}
          opacity="0.85"
          stroke="#ffffff"
          strokeWidth="1.4"
        />
      ))}
      {/* Key labels. */}
      {['D', 'F', 'J', 'K'].map((label, i) => (
        <text
          key={label}
          className="guitar-tutorial-demo-key"
          x={70 + i * 60}
          y="125"
          textAnchor="middle"
        >
          {label}
        </text>
      ))}
      <text
        className="guitar-tutorial-demo-hit"
        x="180"
        y="100"
        textAnchor="middle"
      >
        STRUM!
      </text>
    </svg>
  );
}
